import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Chip, IconButton, Tooltip,
  TextField, MenuItem, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, ButtonGroup,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon, Visibility as ViewIcon, Block as BlockIcon,
  Delete as DeleteIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMovimientoStore } from '../store/movimientoStore';
import { TIPOS_MOVIMIENTO, ESTADOS_MOVIMIENTO } from '../types/movimiento';
import { useCajaBancoStore } from '../../cajas-bancos/store/cajaBancoStore';

const MovimientosPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const {
    movimientos, loading, error, filtro,
    fetchMovimientos, setFiltro, anularMovimiento, deleteMovimiento, clearError,
  } = useMovimientoStore();
  const { cajasBancos, fetchCajasBancos } = useCajaBancoStore();

  const [anularConfirm, setAnularConfirm] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  useEffect(() => {
    fetchMovimientos();
    fetchCajasBancos();
  }, [fetchMovimientos, fetchCajasBancos]);

  const handleAnular = async () => {
    if (anularConfirm) {
      try {
        await anularMovimiento(anularConfirm);
      } catch {
        /* store handles */
      }
    }
    setAnularConfirm(null);
  };

  const handleDelete = async () => {
    if (deleteConfirm) {
      try {
        await deleteMovimiento(deleteConfirm);
      } catch {
        /* store handles */
      }
    }
    setDeleteConfirm(null);
  };

  const columns: GridColDef[] = [
    { field: 'numero', headerName: 'Numero', width: 130 },
    {
      field: 'fechaMovimiento',
      headerName: 'Fecha',
      width: 110,
      valueFormatter: (value: string) =>
        value ? new Date(value).toLocaleDateString('es-PE') : '',
    },
    {
      field: 'tipoMovimiento',
      headerName: 'Tipo',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'INGRESO' ? 'success' : 'error'}
        />
      ),
    },
    { field: 'cajaBancoNombre', headerName: 'Caja/Banco', flex: 1, minWidth: 150 },
    { field: 'nombreAuxiliar', headerName: 'Auxiliar', flex: 1, minWidth: 150 },
    {
      field: 'montoTotal',
      headerName: 'Monto',
      width: 130,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('es-PE', { minimumFractionDigits: 2 }),
    },
    { field: 'monedaSimbolo', headerName: 'Mon.', width: 60 },
    { field: 'glosa', headerName: 'Glosa', flex: 1, minWidth: 150 },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'ACTIVO' ? 'success' : 'default'}
          variant={params.value === 'ANULADO' ? 'outlined' : 'filled'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Ver">
            <IconButton
              size="small"
              onClick={() => navigate(`/tesoreria/movimientos/${params.row.idMovimientoTesoreria}`)}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.estado === 'ACTIVO' && (
            <Tooltip title="Anular">
              <IconButton
                size="small"
                color="warning"
                onClick={() => setAnularConfirm(params.row.idMovimientoTesoreria)}
              >
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeleteConfirm(params.row.idMovimientoTesoreria)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">Movimientos de Tesoreria</Typography>
        <ButtonGroup variant="contained">
          <Button
            color="success"
            startIcon={<AddIcon />}
            onClick={() => navigate('/tesoreria/movimientos/nuevo?tipo=INGRESO')}
          >
            {isMobile ? 'Ingreso' : 'Nuevo Ingreso'}
          </Button>
          <Button
            color="error"
            startIcon={<AddIcon />}
            onClick={() => navigate('/tesoreria/movimientos/nuevo?tipo=EGRESO')}
          >
            {isMobile ? 'Egreso' : 'Nuevo Egreso'}
          </Button>
        </ButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Fecha Inicio"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filtro.fechaInicio || ''}
            onChange={(e) => setFiltro({ fechaInicio: e.target.value })}
          />
          <TextField
            label="Fecha Fin"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filtro.fechaFin || ''}
            onChange={(e) => setFiltro({ fechaFin: e.target.value })}
          />
          <TextField
            label="Caja/Banco"
            select
            size="small"
            sx={{ minWidth: 150 }}
            value={filtro.idCajaBanco || ''}
            onChange={(e) =>
              setFiltro({ idCajaBanco: e.target.value ? parseInt(e.target.value) : undefined })
            }
          >
            <MenuItem value="">Todos</MenuItem>
            {cajasBancos.map((cb) => (
              <MenuItem key={cb.idCajaBanco} value={cb.idCajaBanco}>
                {cb.nombre}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Tipo"
            select
            size="small"
            sx={{ minWidth: 120 }}
            value={filtro.tipoMovimiento || ''}
            onChange={(e) => setFiltro({ tipoMovimiento: e.target.value || undefined })}
          >
            <MenuItem value="">Todos</MenuItem>
            {TIPOS_MOVIMIENTO.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Estado"
            select
            size="small"
            sx={{ minWidth: 120 }}
            value={filtro.estado || ''}
            onChange={(e) => setFiltro({ estado: e.target.value || undefined })}
          >
            <MenuItem value="">Todos</MenuItem>
            {ESTADOS_MOVIMIENTO.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchMovimientos}>
            Buscar
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ height: 'calc(100vh - 300px)' }}>
        <DataGrid
          rows={movimientos}
          columns={columns}
          getRowId={(row) => row.idMovimientoTesoreria}
          loading={loading}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Paper>

      {/* Anular Confirmation */}
      <Dialog open={!!anularConfirm} onClose={() => setAnularConfirm(null)}>
        <DialogTitle>Confirmar Anulacion</DialogTitle>
        <DialogContent>
          <Typography>Esta seguro de anular este movimiento?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnularConfirm(null)}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={handleAnular}>
            Anular
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Confirmar Eliminacion</DialogTitle>
        <DialogContent>
          <Typography>Esta seguro de eliminar este movimiento?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MovimientosPage;
